import { useParams } from 'react-router';
import { WIRE_TYPE_INBOUND } from '../../const/const';
import { Card, CardContent } from '@/components/Card/Card';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { formatAmount } from '@/helpers/formatHelpers';
import { WireInboundTypeIconComponent, WireOutboundTypeIconComponent } from '../WireTypeIconComponent';
import { WireHeadingTitle, WireHeading } from './styles';
import { AssignedWireType } from '@/types/wireTypes';
import { StatusList } from '../StatusList/StatusList';
import { isRecipientProxy } from '@/helpers/wireHelpers';
import { Box } from '@/components/Box/Box';
import { formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { getInboundWireDetailsPath } from '@/bundle/_OrgAdmin/pages/InboundWire/path/path';
import { getOutboundWireDetailsPath } from '@/bundle/_OrgAdmin/pages/OutboundWire/path/path';
import { useStatefulNavigation } from '@/hooks/useStatefulNavigation';

type WiresListType = {
  list: AssignedWireType[];
};
const EMAIL_ADDRESS_LABEL = 'Email Address';

const getEmailColumnName = (isInboundWire: boolean, isProxy: boolean, hasRecipient: boolean) => {
  if (isInboundWire) return `Depositor ${EMAIL_ADDRESS_LABEL}`;

  if (isProxy && hasRecipient) return `Recipient ${EMAIL_ADDRESS_LABEL}`;

  if (isProxy) return `Proxy ${EMAIL_ADDRESS_LABEL}`;

  return `Recipient ${EMAIL_ADDRESS_LABEL}`;
};

const getRecipientEmail = (wire: AssignedWireType) => {
  const { assigned_depositor, assigned_user, assigned_proxy_user } = wire;

  if (assigned_depositor?.user?.email) return assigned_depositor?.user?.email;

  if (!!assigned_proxy_user?.email && !!assigned_user?.email) return assigned_user?.email;

  if (assigned_proxy_user?.email) return assigned_proxy_user?.email;

  if (assigned_user?.email) return assigned_user?.email;

  return '-';
};

export const WiresList = ({ list }: WiresListType) => {
  const { navigateFrom } = useStatefulNavigation();
  const { id } = useParams<{ id: string }>();

  const getWireDetailsUrl = (wire: AssignedWireType) => {
    const { type, id: wireId } = wire;

    return type === WIRE_TYPE_INBOUND ? getInboundWireDetailsPath(id, wireId) : getOutboundWireDetailsPath(id, wireId);
  };

  const navigateToDetails = (wire: AssignedWireType) => {
    const eventData = { status: wire.status, type: wire.type };

    amplitudeService.logEvent(AMPLITUDE_EVENTS.OutboundWireViewDetails, eventData);

    navigateFrom(getWireDetailsUrl(wire));
  };

  return (
    <>
      {list.map((wire) => {
        const { name, date_created, expected_payment_date, amount, type, assigned_user, is_pd_provider_kyc_required } =
          wire;
        const isInboundWire = type === WIRE_TYPE_INBOUND;
        const wireTypeIcon = isInboundWire ? <WireInboundTypeIconComponent /> : <WireOutboundTypeIconComponent />;

        const isProxy = isRecipientProxy(wire);
        const emailColumnName = getEmailColumnName(isInboundWire, isProxy, !!assigned_user);
        const email = getRecipientEmail(wire);
        const verificationMarker = is_pd_provider_kyc_required && 'ID VERIFICATION';

        return (
          <Card
            key={wire.id}
            title={name}
            marker={verificationMarker}
            header={
              <WireHeading>
                {wireTypeIcon}
                <WireHeadingTitle title={name}>{name}</WireHeadingTitle>
              </WireHeading>
            }
            meta={
              <Box display='flex' alignItems='center' columnGap='8px'>
                <StatusList wire={wire} isProxy={isProxy} />
              </Box>
            }
            onClick={() => navigateToDetails(wire)}
          >
            <CardContent name='Creation Date'>{formatDate(date_created)}</CardContent>
            <CardContent name='Expected Date'>{formatDate(expected_payment_date)}</CardContent>
            <CardContent name='Amount'>{formatAmount(amount)}</CardContent>
            <CardContent name={emailColumnName} ratio={2}>
              {email}
            </CardContent>
          </Card>
        );
      })}
    </>
  );
};
